import { Button, Modal, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Category } from "@/sharedTypes";
import { useEffect } from "react";
import GroupSelect from "./GroupSelect";

interface CategoryFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: SubmitHandler<Category>;
  defaultValues?: Category;
  mode?: "create" | "edit";
  errorMessage?: string;
  hasInactiveGroups?: boolean;
}

export default function CategoryFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = "create",
  errorMessage,
  hasInactiveGroups,
}: CategoryFormDialogProps) {
  const { t } = useTranslation(["category"]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Category>();

  useEffect(() => {
    if (open) {
      reset(defaultValues || {
        category_name: "",
        category_description: "",
        group_ids: [],
      });
    }
  }, [open, defaultValues, reset]);

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "edit" ? t("category:editCategory") : t("category:createCategory")}
      </Modal.Header>
      <Modal.Body className="overflow-visible">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="category_name" value={t("category:categoryName")} />
            </div>
            <TextInput
              id="category_name"
              placeholder={t("category:enterCategoryName")}
              {...register("category_name", { required: t("category:categoryNameRequired") })}
              color={errors.category_name ? "failure" : "gray"}
              helperText={errors.category_name?.message}
            />
          </div>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="category_description" value={t("category:categoryDescription")} />
            </div>
            <Textarea
              id="category_description"
              placeholder={t("category:enterCategoryDescription")}
              {...register("category_description", {
                required: t("category:categoryDescriptionRequired"),
              })}
              color={errors.category_description ? "failure" : "gray"}
              helperText={errors.category_description?.message}
            />
          </div>

          <Controller
            control={control}
            name="group_ids"
            rules={{
              validate: (val) =>
                (val && val.length > 0) || t("category:groupRequired"),
            }}
            render={({ field }) => (
              <GroupSelect
                value={field.value ?? []}
                onChange={(val) => field.onChange(val)}
                error={errors.group_ids?.message}
              />
            )}
          />

          {hasInactiveGroups && (
            <p className="text-sm text-yellow-500">
              {t("category:groupInactiveWarning")}
            </p>
          )}

          {errorMessage && (
            <div className="text-sm text-red-500">{errorMessage}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={onClose}>
              {t("category:cancel")}
            </Button>
            <Button type="submit" isProcessing={isSubmitting}>
              {mode === "edit" ? t("category:saveChanges") : t("category:createCategory")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
